import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { Header } from "@/components/Header";
import { Seo } from "@/components/Seo";
import { ArrowRight, BookOpen, LogOut } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const CAT_LABEL = { engineering: "Engineering", trading: "Trading", school: "School", mba: "MBA Finance" };
const BRANCH_LABEL = { common: "All Branches", mechanical: "Mechanical", diploma: "Mech Diploma", civil: "Civil", computer: "Computer/IT", electrical: "Electrical", ec: "E & C", chemical: "Chemical" };
const BOARD_LABEL = { gseb: "GSEB", cbse: "CBSE", icse: "ICSE" };

export const googleSignIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

export const GoogleSignInButton = ({ testId = "google-signin-button", label = "Sign in with Google" }) => (
  <button
    onClick={googleSignIn}
    data-testid={testId}
    className="flex items-center gap-3 border border-white/20 px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-brand"
  >
    <GoogleIcon /> {label}
  </button>
);

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/my/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, [user]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-zinc-600" data-testid="dashboard-loading">Loading...</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-8 px-6" data-testid="dashboard-signin">
        <div className="text-center">
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.4em] text-brand">My Courses</p>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter sm:text-5xl">Sign in to see<br />your courses<span className="text-brand">.</span></h1>
          <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-zinc-500">
            One Google sign-in gathers every course you've bought on ppacedemy.in — progress, quizzes and question banks, all in one place.
          </p>
        </div>
        <GoogleSignInButton testId="dashboard-google-signin" />
        <Link to="/" className="link-underline text-xs uppercase tracking-widest text-zinc-500">Back to home</Link>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <Seo title="My Courses | PP Academy" description="Your purchased courses at PP Academy." path="/dashboard" noindex />
      <Header />
      <section className="min-h-screen px-6 pb-24 pt-32 md:px-12">
        <div className="mx-auto max-w-[1400px]">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-[0.4em] text-brand">My Courses</p>
              <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl" data-testid="dashboard-greeting">
                Hi, {user.name?.split(" ")[0] || "there"}<span className="text-brand">.</span>
              </h1>
              <p className="mt-3 text-sm text-zinc-500" data-testid="dashboard-email">{user.email}</p>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="dashboard-logout-button"
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:border-red-400 hover:text-red-400"
            >
              <LogOut size={14} /> Sign out
            </button>
          </motion.div>

          {courses === null ? (
            <p className="mt-16 text-zinc-600" data-testid="courses-loading">Loading your courses...</p>
          ) : courses.length === 0 ? (
            <div className="mt-16 border border-white/10 p-10 text-center" data-testid="no-courses-yet">
              <BookOpen size={32} className="mx-auto text-brand" />
              <p className="mt-6 font-display text-2xl font-bold tracking-tight">No courses yet on this email.</p>
              <p className="mx-auto mt-3 max-w-md text-sm text-zinc-500">
                Courses you buy with {user.email} will appear here automatically. If you used another email at checkout, open the access link you got after purchase.
              </p>
              <Link to="/" data-testid="browse-courses-link" className="mt-8 inline-flex items-center gap-2 bg-brand px-8 py-3.5 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95">
                Browse Courses <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-courses-grid">
              {courses.map((c, i) => {
                const done = Object.values(c.progress || {}).filter(Boolean).length;
                const pct = c.lessons_count ? Math.round((done / c.lessons_count) * 100) : 0;
                return (
                  <motion.div key={c.access_token} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07, duration: 0.6, ease: EASE }}>
                    <Link to={`/access/${c.access_token}`} data-testid={`my-course-${c.course_slug}`} className="group block">
                      <div className="overflow-hidden border border-white/10">
                        <img src={c.image} alt={c.course_title} loading="lazy" className="aspect-[16/9] w-full object-cover opacity-80 transition-transform duration-700 ease-out group-hover:scale-105" />
                      </div>
                      <div className="mt-4 text-xs uppercase tracking-widest text-brand">
                        {CAT_LABEL[c.category] || c.category}
                        {c.branch ? ` · ${BRANCH_LABEL[c.branch] || c.branch}` : ""}
                        {c.board ? ` · ${BOARD_LABEL[c.board] || c.board}` : ""}
                      </div>
                      <h3 className="mt-2 font-display text-xl font-bold leading-tight tracking-tight">{c.course_title}</h3>
                      <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                        <span>Bought {new Date(c.purchased_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · ₹{c.amount.toLocaleString("en-IN")}</span>
                        <span>{done}/{c.lessons_count} done</span>
                      </div>
                      <div className="mt-2 h-1 w-full bg-white/10">
                        <div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-300 group-hover:text-brand">
                        Continue learning <ArrowRight size={13} />
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
